import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Box,
  IconButton,
  Divider,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { request } from "../../config/request.ts";
import { toast } from "react-toastify";

interface WashData {
  id: number;
  vehicle?: {
    regNo: string;
    customer?: { name: string; phone: string };
  };
  actualAmount?: number | null;
  payedAmount?: number | null;
  washStatus?: string;
  paymentStatus?: string;
}

interface DeleteWashDialogProps {
  open: boolean;
  onClose: () => void;
  wash: WashData | null;
  onSuccess: () => void;
}

export default function DeleteWashDialog({
  open,
  onClose,
  wash,
  onSuccess,
}: DeleteWashDialogProps) {
  const [submitting, setSubmitting] = useState(false);

  const handleDelete = async () => {
    if (!wash) return;
    setSubmitting(true);
    try {
      await request.post("/api/deleteWash", { washId: wash.id });
      toast.success("Wash deleted successfully!");
      onSuccess();
      onClose();
    } catch {
      toast.error("Failed to delete wash");
    } finally {
      setSubmitting(false);
    }
  };

  if (!wash) return null;

  const hasPayment = wash.paymentStatus === "Completed";
  const paidAmount =
    wash.payedAmount != null ? Number(wash.payedAmount).toFixed(2) : "0.00";

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2.5, overflow: "hidden" } }}
    >
      <Box
        sx={{
          background: "linear-gradient(135deg, #ef4444, #b91c1c)",
          px: 2.5,
          py: 2,
          display: "flex",
          alignItems: "center",
          gap: 1.5,
        }}
      >
        <DeleteIcon sx={{ fontSize: 22, color: "#fff" }} />
        <Box
          sx={{ flex: 1, color: "#fff", fontWeight: 700, fontSize: "0.95rem" }}
        >
          Delete Wash
        </Box>
        <IconButton
          onClick={onClose}
          sx={{ color: "rgba(255,255,255,0.8)", p: 0.5 }}
        >
          <CloseIcon sx={{ fontSize: 20 }} />
        </IconButton>
      </Box>

      <DialogContent sx={{ p: 2.5 }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          <Box
            sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.5 }}
          >
            <Info label="Reg No" value={wash.vehicle?.regNo || "-"} />
            <Info
              label="Customer"
              value={wash.vehicle?.customer?.name || "-"}
            />
          </Box>

          <Divider sx={{ borderStyle: "dashed", opacity: 0.5 }} />

          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              gap: 1,
              p: 1.5,
              borderRadius: 1.5,
              bgcolor: "rgba(239, 68, 68, 0.08)",
              border: "1px solid rgba(239, 68, 68, 0.2)",
            }}
          >
            <Box
              sx={{
                width: 28,
                height: 28,
                borderRadius: "50%",
                background: "rgba(239, 68, 68, 0.12)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <WarningAmberIcon sx={{ fontSize: 17, color: "#dc2626" }} />
            </Box>
            <Box
              sx={{
                fontSize: "0.78rem",
                fontWeight: 600,
                color: "#991b1b",
                lineHeight: 1.5,
              }}
            >
              Are you sure you want to delete this wash? This action cannot be
              undone.
            </Box>
          </Box>

          <Box>
            <Box
              sx={{
                fontSize: "0.65rem",
                fontWeight: 700,
                color: "#9ca3af",
                textTransform: "uppercase",
                letterSpacing: 0.3,
                mb: 0.75,
              }}
            >
              Following will also be removed
            </Box>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
              <Bullet text="Wash images and stored image files" />
              <Bullet text="Payment records of this wash" />
              <Bullet text="Income transactions and the linked account balance" />
              {hasPayment && (
                <Bullet text="Customer wash count used for offers" />
              )}
            </Box>
          </Box>

          {hasPayment && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                p: 1,
                borderRadius: 1.5,
                bgcolor: "rgba(0,0,0,0.03)",
              }}
            >
              <Box
                sx={{
                  fontSize: "0.72rem",
                  fontWeight: 600,
                  color: "#64748b",
                }}
              >
                Paid Amount
              </Box>
              <Box sx={{ fontSize: "0.85rem", fontWeight: 700, color: "#1c1b1f" }}>
                ₹{Number(paidAmount).toLocaleString()}
              </Box>
            </Box>
          )}
        </Box>
      </DialogContent>

      <Divider />

      <DialogActions sx={{ p: 2, gap: 1 }}>
        <Button
          onClick={onClose}
          variant="outlined"
          size="small"
          sx={{
            borderColor: "rgba(0,0,0,0.12)",
            color: "#374151",
            textTransform: "none",
            fontWeight: 600,
            borderRadius: 1.5,
            px: 2,
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleDelete}
          variant="contained"
          size="small"
          disabled={submitting}
          startIcon={
            submitting ? (
              <CircularProgress size={14} color="inherit" />
            ) : (
              <DeleteIcon />
            )
          }
          sx={{
            background: "linear-gradient(135deg, #ef4444, #b91c1c)",
            textTransform: "none",
            fontWeight: 700,
            borderRadius: 1.5,
            px: 2,
            "&:hover": {
              background: "linear-gradient(135deg, #b91c1c, #991b1b)",
            },
          }}
        >
          {submitting ? "Deleting..." : "Yes, Delete"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function Info({ label, value }: { label: string; value: string }) {
  return (
    <Box>
      <Box
        sx={{
          fontSize: "0.65rem",
          fontWeight: 700,
          color: "#9ca3af",
          textTransform: "uppercase",
          letterSpacing: 0.3,
          mb: 0.15,
        }}
      >
        {label}
      </Box>
      <Box
        sx={{
          fontSize: "0.82rem",
          fontWeight: 600,
          color: "#1c1b1f",
          wordBreak: "break-word",
        }}
      >
        {value}
      </Box>
    </Box>
  );
}

function Bullet({ text }: { text: string }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
      <Box
        sx={{
          width: 5,
          height: 5,
          borderRadius: "50%",
          background: "#ef4444",
          flexShrink: 0,
        }}
      />
      <Box sx={{ fontSize: "0.75rem", fontWeight: 500, color: "#475569" }}>
        {text}
      </Box>
    </Box>
  );
}
